import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, X, ArrowLeft, Plus, Search, Users, Bot, Settings as SettingsIcon, Volume2, Bell, BellOff, Mic, Pin, PinOff, Zap, Smile } from "lucide-react";
import { GrupoAvatar } from "./GrupoAvatar";
import { listarConversas, ConversaResumo, abrirOuCriarDM, abrirChatIA, fixarConversa, desfixarConversa } from "../services/chat";
import { useQueryClient } from "@tanstack/react-query";
import { listarPessoas, PessoaOnline } from "../services/presence";
import { useAuth } from "../hooks/useAuth";
import { useChatUiStore } from "../store/chatUiStore";
import { ChatThread } from "../pages/chat/ChatThread";
import { tituloConversa } from "../pages/chat/ChatPage";
import { NovaConversaModal } from "../pages/chat/NovaConversaModal";
import { Avatar } from "./Avatar";
import { useMutation } from "@tanstack/react-query";
import { getSomAtivo, setSomAtivo, getInPageNotifAtivo, setInPageNotifAtivo, getAudioGravacaoAtivo, setAudioGravacaoAtivo, getSomVolume, setSomVolume, getSomTipo, setSomTipo, getAbrirDiretoAtivo, setAbrirDiretoAtivo, getEmojiPickerAtivo, setEmojiPickerAtivo } from "../hooks/useChatNotifications";
import { tocarSom, TIPOS_SOM, TipoSom } from "../utils/sons";
import { usePush } from "../hooks/usePush";
import { useEscape } from "../hooks/useEscape";
import { ChatContextMenu } from "./ChatContextMenu";

type Aba = "tudo" | "grupos" | "pessoas";

export function ChatFloating() {
  const { isOpen, conversaId, abrir, fechar, abrirConversa, voltarParaLista } = useChatUiStore();
  const { user } = useAuth();

  const { data: conversas = [] } = useQuery({
    queryKey: ["chat-conversas"],
    queryFn: listarConversas,
    refetchInterval: 15000,
  });

  const totalNaoLidas = conversas.reduce((s, c) => s + (c.naoLidas || 0), 0);

  // Clicar no botão flutuante:
  // - Se a opção "abrir direto" está ligada (default) E há não-lidas → abre a conversa
  //   que chamou primeiro (mais antiga). Sem não lidas → abre a lista.
  // - Se a opção está desligada → sempre abre a lista, deixando o usuário escolher.
  function abrirInteligente() {
    const abrirDireto = getAbrirDiretoAtivo();
    const naoLidas = conversas.filter((c) => (c.naoLidas || 0) > 0);
    if (!abrirDireto || naoLidas.length === 0) { abrir(); return; }
    const primeira = naoLidas
      .slice()
      .sort((a, b) => {
        const aT = a.mensagens?.[0]?.criadoEm ? new Date(a.mensagens[0].criadoEm).getTime() : 0;
        const bT = b.mensagens?.[0]?.criadoEm ? new Date(b.mensagens[0].criadoEm).getTime() : 0;
        return aT - bT;
      })[0];
    abrirConversa(primeira.id);
  }

  // ESC funciona como "voltar":
  // 1) Modais e overlays internos (NovaConversa, Participantes, Lightbox, Config…)
  //    se registram em useEscape e fecham primeiro.
  // 2) Sem overlays e dentro de uma conversa: volta pra lista.
  // 3) Já na lista (sem nada por cima): aí sim fecha o chat.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (conversaId) voltarParaLista();
      else fechar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, conversaId, fechar, voltarParaLista]);

  // Sinaliza no <body> que o painel está aberto — outros componentes (ex.: o
  // toast de notificação) reposicionam pra não sobrepor.
  useEffect(() => {
    if (isOpen) document.body.setAttribute("data-chat-aberto", "1");
    else document.body.removeAttribute("data-chat-aberto");
    return () => { document.body.removeAttribute("data-chat-aberto"); };
  }, [isOpen]);

  if (!isOpen) {
    return (
      <div className="chat-scope contents">
      <button
        onClick={abrirInteligente}
        className="fixed bottom-5 right-5 z-30 w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Abrir chat"
        title={totalNaoLidas > 0 ? `${totalNaoLidas} mensagem(ns) não lida(s)` : "Chat"}
      >
        <MessageSquare size={22} />
        {totalNaoLidas > 0 && (
          <>
            {/* Pulso atrás (bolinha vermelha que pisca) */}
            <span className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 rounded-full animate-ping opacity-75" />
            {/* Badge com o contador por cima */}
            <span className="absolute -top-1.5 -right-1.5 min-w-[24px] h-6 px-1.5 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-md">
              {totalNaoLidas > 99 ? "99+" : totalNaoLidas}
            </span>
          </>
        )}
      </button>
      </div>
    );
  }

  return (
    <div
      className="
        fixed z-40 bg-white dark:bg-slate-900 flex flex-col overflow-hidden min-h-0
        inset-0 w-full h-full rounded-none border-0
        sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[360px] sm:max-w-[calc(100vw-2.5rem)] sm:h-[560px] sm:max-h-[calc(100vh-3rem)]
        sm:rounded-xl sm:shadow-2xl sm:border sm:border-slate-200 sm:dark:border-slate-700
      "
    >
      {conversaId ? (
        <PainelConversa
          conversaId={conversaId}
          onVoltar={voltarParaLista}
          onFechar={fechar}
          ehUnicaConversa={false}
        />
      ) : (
        <PainelLista
          conversas={conversas}
          userId={user?.id}
          totalNaoLidas={totalNaoLidas}
          onAbrirConversa={abrirConversa}
          onFechar={fechar}
        />
      )}
    </div>
  );
}

function PainelConversa({ conversaId, onVoltar, onFechar }: {
  conversaId: string;
  onVoltar: () => void;
  onFechar: () => void;
  ehUnicaConversa: boolean;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Lê a conversa do cache pra saber se está fixada (sem fetch extra)
  const conversas = qc.getQueryData<ConversaResumo[]>(["chat-conversas"]) || [];
  const conv = conversas.find((c) => c.id === conversaId);
  const meuPart = conv?.participantes.find((p) => p.usuarioId === user?.id);
  const fixada = !!meuPart?.fixadoEm;

  async function toggleFixar() {
    try {
      if (fixada) await desfixarConversa(conversaId);
      else await fixarConversa(conversaId);
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    } catch {}
  }

  return (
    <>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 shrink-0">
        <button onClick={onVoltar} className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Voltar">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1" />
        {conv && conv.tipo !== "ia" && (
          <button
            onClick={toggleFixar}
            className={`p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 ${fixada ? "text-amber-600" : "text-slate-500"}`}
            title={fixada ? "Desafixar" : "Fixar conversa no topo"}
          >
            {fixada ? <PinOff size={14} /> : <Pin size={14} />}
          </button>
        )}
        <button onClick={onFechar} className="p-2 sm:p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Fechar">
          <X size={20} className="sm:hidden" />
          <X size={16} className="hidden sm:inline" />
        </button>
      </div>
      <ChatThread conversaId={conversaId} onVoltar={onVoltar} />
    </>
  );
}

function PainelLista({ conversas, userId, totalNaoLidas, onAbrirConversa, onFechar }: {
  conversas: ConversaResumo[];
  userId?: string;
  totalNaoLidas: number;
  onAbrirConversa: (id: string) => void;
  onFechar: () => void;
}) {
  const [aba, setAba] = useState<Aba>("tudo");
  const [busca, setBusca] = useState("");
  const [showNova, setShowNova] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [menuCtx, setMenuCtx] = useState<{ conversa: ConversaResumo; x: number; y: number } | null>(null);

  const { data: pessoas = [] } = useQuery({
    queryKey: ["presence"],
    queryFn: listarPessoas,
    refetchInterval: 30000,
  });
  const onlineMap = useMemo(() => new Map(pessoas.map((p) => [p.id, p.online])), [pessoas]);

  const filtradas = useMemo(() => {
    let lista = conversas.filter((c) => c.tipo !== "projeto");
    if (aba === "grupos") lista = lista.filter((c) => c.tipo === "grupo");
    if (busca.trim()) {
      const t = busca.trim().toLowerCase();
      lista = lista.filter((c) => tituloConversa(c, userId).toLowerCase().includes(t));
    }
    return lista;
  }, [conversas, aba, busca, userId]);

  return (
    <div className="chat-scope contents">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-600 to-blue-500 text-white shrink-0">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          <MessageSquare size={14} /> Chat
          {totalNaoLidas > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{totalNaoLidas}</span>
          )}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className={`p-1.5 rounded hover:bg-white/20 ${showConfig ? "bg-white/20" : ""}`}
            title="Configurações"
          >
            <SettingsIcon size={14} />
          </button>
          <button
            onClick={() => setShowNova(true)}
            className="p-1.5 rounded hover:bg-white/20"
            title="Nova conversa"
          >
            <Plus size={14} />
          </button>
          <button onClick={onFechar} className="p-1.5 sm:p-1.5 p-2 rounded hover:bg-white/20" title="Fechar">
            <X size={18} className="sm:hidden" />
            <X size={14} className="hidden sm:inline" />
          </button>
        </div>
      </div>

      {showConfig && <ConfigPanel onClose={() => setShowConfig(false)} />}

      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 space-y-1.5 shrink-0">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar…"
            className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 text-xs outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-800 rounded p-0.5">
          {(["tudo", "grupos", "pessoas"] as Aba[]).map((t) => (
            <button
              key={t}
              onClick={() => setAba(t)}
              className={`flex-1 text-[10px] font-medium py-1 rounded uppercase ${
                aba === t ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-300 shadow-sm" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {t === "tudo" ? "Tudo" : t === "grupos" ? "Grupos" : "Pessoas"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {aba === "pessoas" ? (
          <PessoasLista busca={busca} userId={userId} onAbrirConversa={onAbrirConversa} />
        ) : (
          <>
            {filtradas.length === 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-8 px-4">
                {busca ? "Nada encontrado" : "Nenhuma conversa ainda"}
              </p>
            )}
            {filtradas.map((c) => (
              <ConversaItemMini
                key={c.id}
                c={c}
                userId={userId}
                onlineMap={onlineMap}
                onClick={() => onAbrirConversa(c.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenuCtx({ conversa: c, x: e.clientX, y: e.clientY });
                }}
              />
            ))}
          </>
        )}
      </div>

      {showNova && <NovaConversaModal onClose={() => setShowNova(false)} />}
      {menuCtx && (
        <ChatContextMenu
          conversa={menuCtx.conversa}
          userId={userId}
          x={menuCtx.x}
          y={menuCtx.y}
          onClose={() => setMenuCtx(null)}
        />
      )}
    </div>
  );
}

function ConfigPanel({ onClose }: { onClose: () => void }) {
  const [som, setSom] = useState(getSomAtivo());
  const [inPage, setInPage] = useState(getInPageNotifAtivo());
  const [audio, setAudio] = useState(getAudioGravacaoAtivo());
  const [volume, setVolume] = useState(getSomVolume());
  const [tipoSom, setTipoSom] = useState<TipoSom>(getSomTipo());
  const [abrirDireto, setAbrirDireto] = useState(getAbrirDiretoAtivo());
  const [emojiPicker, setEmojiPicker] = useState(getEmojiPickerAtivo());
  const push = usePush();
  useEscape(true, onClose);

  return (
    <div className="px-3 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-2.5 shrink-0">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase text-slate-600 dark:text-slate-300">Notificações</p>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500" title="Fechar config">
          <X size={12} />
        </button>
      </div>

      {/* Som */}
      <ConfigLinha
        icon={<Volume2 size={14} className="text-slate-500" />}
        titulo="Som das mensagens"
        sub="Toca um ding ao receber"
        botao={
          <input
            type="checkbox"
            checked={som}
            onChange={(e) => { setSomAtivo(e.target.checked); setSom(e.target.checked); }}
            className="accent-blue-600"
          />
        }
      />

      {/* Volume + tipo de som — só aparecem se Som estiver ligado */}
      {som && (
        <div className="pl-6 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 w-14 shrink-0">Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => {
                const v = Number(e.target.value);
                setVolume(v);
                setSomVolume(v);
              }}
              className="flex-1 accent-blue-600"
            />
            <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400 w-8 text-right">{Math.round(volume * 100)}%</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500 dark:text-slate-400 w-14 shrink-0">Tom</span>
            <select
              value={tipoSom}
              onChange={(e) => {
                const t = e.target.value as TipoSom;
                setTipoSom(t);
                setSomTipo(t);
                tocarSom(t, volume); // pré-escuta ao mudar
              }}
              className="flex-1 text-xs px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 outline-none"
            >
              {TIPOS_SOM.map((t) => (
                <option key={t.valor} value={t.valor}>{t.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => tocarSom(tipoSom, volume)}
              className="text-[11px] px-2 py-1 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300"
              title="Tocar pra testar"
            >
              Testar
            </button>
          </div>
        </div>
      )}

      {/* Gravação de áudio */}
      <ConfigLinha
        icon={<Mic size={14} className="text-slate-500" />}
        titulo="Gravação de áudio"
        sub="Mostra o botão de gravar no chat"
        botao={
          <input
            type="checkbox"
            checked={audio}
            onChange={(e) => { setAudioGravacaoAtivo(e.target.checked); setAudio(e.target.checked); }}
            className="accent-blue-600"
          />
        }
      />

      {/* Picker de emoji no composer */}
      <ConfigLinha
        icon={<Smile size={14} className="text-slate-500" />}
        titulo="Picker de emoji"
        sub="Mostra o botão de emojis ao lado do anexo no composer"
        botao={
          <input
            type="checkbox"
            checked={emojiPicker}
            onChange={(e) => { setEmojiPickerAtivo(e.target.checked); setEmojiPicker(e.target.checked); }}
            className="accent-blue-600"
          />
        }
      />

      {/* Abrir direto a conversa que chamou */}
      <ConfigLinha
        icon={<Zap size={14} className="text-slate-500" />}
        titulo="Abrir direto quem chamou"
        sub="Ao clicar no botão flutuante, vai direto pra conversa não lida mais antiga"
        botao={
          <input
            type="checkbox"
            checked={abrirDireto}
            onChange={(e) => { setAbrirDiretoAtivo(e.target.checked); setAbrirDireto(e.target.checked); }}
            className="accent-blue-600"
          />
        }
      />

      {/* Toast in-page */}
      <ConfigLinha
        icon={<Bell size={14} className="text-slate-500" />}
        titulo="Toast no navegador"
        sub="Aparece quando aba está fora de foco"
        botao={
          <input
            type="checkbox"
            checked={inPage}
            onChange={async (e) => {
              const v = e.target.checked;
              if (v && "Notification" in window && Notification.permission !== "granted") {
                await Notification.requestPermission();
              }
              setInPageNotifAtivo(v);
              setInPage(v);
            }}
            className="accent-blue-600"
          />
        }
      />

      {/* Push do browser */}
      <ConfigLinha
        icon={push.status === "ativo" ? <Bell size={14} className="text-emerald-600" /> : <BellOff size={14} className="text-slate-500" />}
        titulo="Push do navegador"
        sub={
          push.status === "ativo" ? "Recebe mesmo com aba fechada" :
          push.status === "negado" ? "Bloqueado — desbloqueie nas configs do browser" :
          push.status === "indisponivel" ? "Exige HTTPS ou localhost" :
          push.status === "carregando" ? "Verificando…" :
          "Inativo"
        }
        botao={
          <button
            onClick={async () => {
              if (push.status === "ativo") await push.desativar();
              else await push.ativar();
            }}
            disabled={push.status === "negado" || push.status === "indisponivel" || push.status === "carregando"}
            className={`text-[10px] px-2 py-0.5 rounded font-medium ${
              push.status === "ativo" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {push.status === "ativo" ? "Desativar" : push.status === "negado" ? "Bloqueado" : push.status === "indisponivel" ? "Indisp." : "Ativar"}
          </button>
        }
      />
    </div>
  );
}

function ConfigLinha({ icon, titulo, sub, botao }: { icon: React.ReactNode; titulo: string; sub?: string; botao: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{titulo}</p>
        {sub && <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{sub}</p>}
      </div>
      <div className="shrink-0 mt-0.5">{botao}</div>
    </div>
  );
}

function ConversaItemMini({ c, userId, onlineMap, onClick, onContextMenu }: {
  c: ConversaResumo;
  userId?: string;
  onlineMap?: Map<string, boolean>;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const titulo = tituloConversa(c, userId);
  const ultima = c.mensagens?.[0];
  const previewBase = ultima?.anexos?.length
    ? `📎 ${ultima.anexos[0].tipo}`
    : ultima?.texto?.slice(0, 50) || "";
  const autorPrefix = ultima?.autorIa
    ? "IA: "
    : ultima?.autor?.nome && c.tipo !== "dm"
      ? `${ultima.autor.nome.split(" ")[0]}: `
      : "";
  const Icon = c.tipo === "ia" ? Bot : Users;
  // O campo fixadoEm vive no participante (por usuário), não no Conversa.
  // Antes lia `(c as any).fixadoEm` que sempre era undefined → pin nunca aparecia.
  const meuFixadoEm = c.participantes.find((p) => p.usuarioId === userId)?.fixadoEm;
  const fixada = !!meuFixadoEm;

  // Para DM, descobre o outro participante e seu status online
  let outroOnline: boolean | null = null;
  let outroFotoUrl: string | null = null;
  let outroNome: string | null = null;
  if (c.tipo === "dm") {
    const outro = c.participantes.find((p) => p.usuarioId !== userId);
    if (outro) {
      if (onlineMap) outroOnline = onlineMap.get(outro.usuarioId) ?? false;
      outroFotoUrl = (outro.usuario as any)?.fotoUrl || null;
      outroNome = outro.usuario?.nome || null;
    }
  }

  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={`w-full text-left px-3 py-2 border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 ${fixada ? "bg-amber-50/40 dark:bg-amber-900/10" : ""}`}
    >
      <div className="relative shrink-0">
        {c.tipo === "dm" && outroNome ? (
          <Avatar nome={outroNome} fotoUrl={outroFotoUrl} tamanho={32} bgFallback="bg-blue-100" textFallback="text-blue-700" />
        ) : c.tipo === "grupo" ? (
          <GrupoAvatar fotoUrl={c.fotoUrl} tamanho={32} titulo={titulo} />
        ) : (
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            c.tipo === "ia"
              ? "bg-gradient-to-br from-blue-500 to-blue-700 text-white"
              : "bg-blue-100 text-blue-700"
          }`}>
            <Icon size={14} />
          </div>
        )}
        {outroOnline !== null && (
          <span
            className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-900 ${outroOnline ? "bg-emerald-500" : "bg-slate-300"}`}
            title={outroOnline ? "Online" : "Offline"}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate flex-1 flex items-center gap-1">
            {fixada && <Pin size={10} className="text-amber-600 shrink-0" />}
            {titulo}
          </p>
          {ultima?.criadoEm && (
            <span className="text-[9px] text-slate-400 shrink-0">{formatarHora(ultima.criadoEm)}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate flex-1">
            {autorPrefix}{previewBase || <span className="italic">sem mensagens</span>}
          </p>
          {(c.naoLidas || 0) > 0 && (
            <span className="bg-blue-600 text-white text-[9px] font-bold rounded-full px-1.5 shrink-0">{c.naoLidas}</span>
          )}
        </div>
      </div>
    </button>
  );
}

function PessoasLista({ busca, userId, onAbrirConversa }: { busca: string; userId?: string; onAbrirConversa: (id: string) => void }) {
  const { data: pessoas = [], isLoading } = useQuery({
    queryKey: ["presence"],
    queryFn: listarPessoas,
    refetchInterval: 30000,
  });

  const dmM = useMutation({
    mutationFn: (id: string) => abrirOuCriarDM(id),
    onSuccess: (c) => onAbrirConversa(c.id),
  });
  const iaM = useMutation({
    mutationFn: () => abrirChatIA(),
    onSuccess: (c) => onAbrirConversa(c.id),
  });

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return pessoas
      .filter((p) => p.id !== userId)
      .filter((p) => !t || p.nome.toLowerCase().includes(t) || p.email.toLowerCase().includes(t));
  }, [pessoas, busca, userId]);

  const online = filtradas.filter((p) => p.online);
  const offline = filtradas.filter((p) => !p.online);
  const buscaIa = !busca || "ia inteligencia artificial robo".includes(busca.trim().toLowerCase());

  return (
    <div>
      {buscaIa && (
        <>
          <p className="px-3 py-1 text-[9px] uppercase font-bold text-purple-700 bg-purple-50 dark:bg-purple-900/20">Assistente</p>
          <button
            onClick={() => iaM.mutate()}
            className="w-full text-left px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-b border-slate-50 dark:border-slate-800"
          >
            <div className="relative shrink-0">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white">
                <Bot size={14} />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">IA</p>
              <p className="text-[10px] text-slate-500 truncate">Sempre online · responde tudo</p>
            </div>
          </button>
        </>
      )}
      {isLoading && <p className="text-xs text-slate-400 text-center py-8">Carregando…</p>}
      {online.length > 0 && (
        <>
          <p className="px-3 py-1 text-[9px] uppercase font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20">Online · {online.length}</p>
          {online.map((p) => <PessoaItemMini key={p.id} p={p} onClick={() => dmM.mutate(p.id)} />)}
        </>
      )}
      {offline.length > 0 && (
        <>
          <p className="px-3 py-1 text-[9px] uppercase font-bold text-slate-500 bg-slate-50 dark:bg-slate-800">Offline · {offline.length}</p>
          {offline.map((p) => <PessoaItemMini key={p.id} p={p} onClick={() => dmM.mutate(p.id)} />)}
        </>
      )}
    </div>
  );
}

function PessoaItemMini({ p, onClick }: { p: PessoaOnline; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 border-b border-slate-50 dark:border-slate-800"
    >
      <div className="relative shrink-0">
        <Avatar nome={p.nome} fotoUrl={p.fotoUrl} tamanho={28} bgFallback="bg-blue-100" textFallback="text-blue-700" />
        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${p.online ? "bg-emerald-500" : "bg-slate-300"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-800 dark:text-slate-100 truncate">{p.nome}</p>
        <p className="text-[10px] text-slate-500 truncate">{p.perfil}</p>
      </div>
    </button>
  );
}

function formatarHora(iso: string) {
  const d = new Date(iso);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}
